import { useMutation, useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Breadcrumb,
  Button,
  MenuButton,
  Modal,
  ResponsivePrimaryNav,
  SearchResultNavigation,
} from "../../../components/ui";
import {
  FETCH_INVOICE as fetchInvoice,
  FETCH_INVOICES_LIST as fetchInvoicesList,
} from "../../../graphql/query/Invoice";
import useSearchResultNavigation from "../../../hooks/useSearchResultNavigation";
import routes from "../pages/Wizard/routes";
import AddInvoice from "./Actions/AddInvoice";
import ChangeToInvoice from "./Actions/ChangeToInvoice";
import CreateShipmentWork from "./Actions/CreateShipmentWork";
// import ExportInvoiceReport from './Actions/ExportInvoiceReport'
import ExportActiveReport from "../../../components/Modals/ExportActiveReport";
import { GenerateReportBody } from "../../../components/Modals/GenerateReport";
import { RE_INVOICE as reInvoiceMutation } from "../../../graphql/mutation/Invoice";
import useUser from "../../../hooks/useUser";
import AdhocReportBody from "./Actions/AdhocReportBody";
import exportModule from "./Actions/ExportModule";
import ExportSageAccounts from "./Actions/ExportSageAccounts";
import RemoveItem from "./Actions/RemoveItem";
import InvoiceSearch from "./InvoiceSearch";
import { buildInvoiceNavigationTargetUrl } from "./searchResultNavigation";
import {
  invoiceFilterStore,
  invoiceNavigationStore,
} from "./useInvoiceFilters";
import { clearSelectionState } from "../../../util/clearSelectionState";
import { scopeBulkActionTargets } from "../../../util/scopeBulkActionTargets";

const fetchInvoicesNavigation = fetchInvoicesList({});

const PrimaryNav = props => {
  const { formik } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const pathnames = location?.pathname.split("/");
  const search = invoiceFilterStore.get();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isExportReportOpen, setIsExportReportOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { hasPermission } = useUser();

  const [reInvoice, { loading, error, reset }] =
    useMutation(reInvoiceMutation());

  // get id from url
  const invoiceId = pathnames?.length > 2 ? pathnames[2] : null;
  const invoiceIdNumber = invoiceId ? Number(invoiceId) : null;
  // fetch invoice details if id is available
  const { data: { invoice } = {} } = useQuery(
    fetchInvoice({
      bank: { currency: {} },
      company: { images: {} },
      contact: {},
      country: {},
      currency: {},
      invoiceTotals: {},
      invoiceSalesPersons: {
        salesUser: {},
      },
      salesUser: {},
      type: {},
    }),
    {
      skip: !invoiceId,
      variables: { id: +invoiceId },
    },
  );

  // find current submodule route
  const path = pathnames[pathnames?.length - 1];
  const route = routes?.find(item => item?.href?.includes(path));
  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams.get("status") || "all";
  const listContext = useMemo(
    () => ({ locationSearch: location.search }),
    [location.search],
  );
  const {
    hasNavigationContext,
    hasNext,
    hasPrevious,
    navigationLoading,
    navigateToNext,
    navigateToPrevious,
    showNavigationControls,
  } = useSearchResultNavigation({
    activeSubPage: route?.href || "details",
    buildTargetUrl: buildInvoiceNavigationTargetUrl,
    dataKey: "invoicesV2",
    fetchQuery: fetchInvoicesNavigation,
    listContext,
    navigate,
    navigationStore: invoiceNavigationStore,
    recordId: invoiceIdNumber,
    status,
  });
  const selected = Object.keys(formik?.values?.selected?.ids || {});
  const listId = +(
    queryParams.get("listId") ||
    (status === "selectListWorking" ? queryParams.get("workingListId") : null)
  );
  const ids = selected?.map(key => {
    const obj = formik?.values?.selected?.ids?.[key];
    return obj?.item?.id || obj?.id;
  });
  const select = formik?.values?.selected?.select;

  const statusMap = {
    all: "All Invoices",
    searched: "Searched Invoices",
    selectListWorking: "Select List (Working)",
  };
  const label = statusMap[status] || "All Invoices";

  // prepare steps for breadcrumbs
  const steps = [
    {
      label,
      onClick: () =>
        navigate(`/invoice${location.search || `?status=${status}`}`),
    },
    ...(invoice
      ? [
          {
            label: invoice?.id,
            onClick: () =>
              navigate(
                buildInvoiceNavigationTargetUrl({
                  listContext,
                  recordId: invoice?.id,
                  status,
                }),
              ),
          },
          {
            label: route?.name,
            onClick: () =>
              navigate(
                buildInvoiceNavigationTargetUrl({
                  activeSubPage: route?.href,
                  listContext,
                  recordId: invoice?.id,
                  status,
                }),
              ),
          },
        ]
      : []),
  ];

  const breadcrumbProps = {
    currentStepIndex: steps?.length - 1,
    onChange: index => steps[index]?.onClick(),
    steps: steps?.map(item => item?.label),
  };

  // Detail pages share Formik with the list view, so bulk-action props must
  // be scoped to the URL id; otherwise list-side state leaks into actions
  // here. See scopeBulkActionTargets.
  const scopedTargets = scopeBulkActionTargets({
    entityId: invoiceId,
    selectedIds: ids,
    fallbacks: { listId, search, select },
  });

  const defaultActionProps = {
    closeOnBackdrop: false,
    hideCloseButton: true,
    ids: scopedTargets.ids,
    listId: scopedTargets.listId,
    onClose: () => {
      formik?.resetForm();
    },
    scale: "md",
    search: scopedTargets.search,
    select: scopedTargets.select,
  };

  const ReInvoiceModalProps = {
    title: error ? "Error" : "Duplicate Invoice?",
    scale: "sm",
    description: error
      ? error?.message
      : `Are you sure you want to duplicate this invoice?`,
    closeOnBackdrop: true,
    body: ({ closeModal }) => {
      const handleDuplicate = () => {
        reInvoice({
          variables: {
            input: { id: invoice?.id },
          },
          update: (cache, { data }) => {
            const { reInvoice: { invoice, success } = {} } = data || {};
            if (success) {
              closeModal();
              reset();
              navigate(`${invoice?.id}/details`);
            }
          },
        });
      };

      return (
        <div className="mt-4 flex">
          <Button
            className="mr-3"
            label={error ? "Close" : "Cancel"}
            action={error ? "primary" : "default"}
            onClick={() => {
              closeModal?.();
              reset();
            }}
          />
          {!error && (
            <Button
              label={loading ? "Duplicating" : "Duplicate"}
              disabled={loading}
              onClick={handleDuplicate}
            />
          )}
        </div>
      );
    },
  };

  const actionMenuProps = {
    label: "Actions",
    options: [
      {
        label: "Add Invoice",
        disabled: !hasPermission("UPDATE_INVOICE"),
        modalProps: {
          body: AddInvoice,
          closeOnBackdrop: false,
          hideCloseButton: true,
          scale: "md",
          isCreateOpen,
          onOpen: () => {
            setIsCreateOpen(true);
          },
          onClose: () => {
            setIsCreateOpen(false);
          },
        },
      },
      {
        disabled: !ids?.length,
        label: "Create an Adhoc Report",
        modalProps: {
          body: AdhocReportBody,
          ...defaultActionProps,
          exportModule,
        },
      },
      {
        disabled: !invoice?.id && !ids?.length,
        label: "Export Invoice Report",
        modalProps: {
          ...defaultActionProps,
          body: ExportActiveReport,
          optimise: true,
          type: "Invoice",
          ids: invoice?.id ? [invoice?.id] : ids,
          isExportReportOpen,
          onOpen: () => {
            setIsExportReportOpen(true);
          },
          onClose: () => {
            setIsExportReportOpen(false);
          },
        },
      },
      {
        disabled: !invoice?.id && !ids?.length,
        label: (
          <>
            Reporting{" "}
            <span
              className={`ml-1 inline-flex items-center rounded-full px-1.5 text-[8px] font-semibold leading-4 ${
                !invoice?.id && !ids?.length
                  ? "bg-gray-100 text-gray-400"
                  : "bg-green-100 text-green-700"
              }`}
            >
              BETA
            </span>
          </>
        ),
        modalProps: {
          ...defaultActionProps,
          body: GenerateReportBody,
          type: "Invoice",
          ids: invoice?.id ? [invoice?.id] : ids,
          onClose: () => {},
        },
      },
      {
        label: "Export to Line 200",
        modalProps: {
          body: ExportSageAccounts,
          companyId: invoice?.companyId,
          title: "Export Sage Accounts",
          scale: "sm",
          description: "Export Line Items",
          closeOnBackdrop: true,
          hideCloseButton: true,
        },
      },
      ...(invoice?.id
        ? [
            // {
            //   label: 'Export Report',
            //   modalProps: {
            //     body: ExportInvoiceReport,
            //     closeOnBackdrop: false,
            //     hideCloseButton: true,
            //     invoice,
            //     scale: "md",
            //   },
            // },
            {
              label: "Copy JSON",
              onClick: () =>
                navigator.clipboard.writeText(JSON.stringify(invoice)),
            },
            {
              disabled: invoice?.type?.id === 1,
              label: "Change Proforma to Invoice",
              modalProps: {
                body: ChangeToInvoice,
                closeOnBackdrop: false,
                invoice,
                hideCloseButton: true,
                scale: "md",
              },
            },
            {
              label: "Create Shipment Work",
              modalProps: {
                body: CreateShipmentWork,
                closeOnBackdrop: false,
                invoice,
                hideCloseButton: true,
                scale: "md",
              },
            },
            {
              label: "Remove Item",
              modalProps: {
                body: RemoveItem,
                closeOnBackdrop: false,
                invoice,
                hideCloseButton: true,
                scale: "md",
              },
            },
            {
              label: "ReInvoice",
              modalProps: ReInvoiceModalProps,
            },
          ]
        : []),
    ],
  };

  const searchModalProps = {
    body: InvoiceSearch,
    closeOnBackdrop: true,
    scale: "fullscreen",
    isSearchOpen,
    onClose: () => setIsSearchOpen(false),
    onSearchSubmitted: () => clearSelectionState(formik),
  };

  return (
    <ResponsivePrimaryNav>
      <div>
        <Breadcrumb {...breadcrumbProps} />
      </div>
      <div className="flex items-center gap-2 sm:gap-4 lg:justify-end">
        <div id="form-save-status" />
        <SearchResultNavigation
          hasNavigationContext={hasNavigationContext}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          navigationLoading={navigationLoading}
          navigateToNext={navigateToNext}
          navigateToPrevious={navigateToPrevious}
          showNavigationControls={showNavigationControls}
        />
        <Modal {...searchModalProps}>
          <Button
            label="Invoice Search"
            action="black"
            onClick={() => setIsSearchOpen(true)}
          />
        </Modal>
        <MenuButton {...actionMenuProps} />
      </div>
    </ResponsivePrimaryNav>
  );
};

export default PrimaryNav;
